import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, FileText, Wrench, Banknote, StickyNote } from "lucide-react";
import SearchInput from "@/components/dashboard/SearchInput";
import { StatusPill, fmtKr, safeNum, orDash } from "@/components/dashboard/Primitives";
import { fetchCustomerMaterials } from "@/lib/api";

export default function CustomerDetailModal({ customer, allJobs = [], allJobDetails = {}, open, onClose }) {
    const [matSearch, setMatSearch] = useState("");
    const [invSearch, setInvSearch] = useState("");
    const [jobSearch, setJobSearch] = useState("");
    const [matData, setMatData] = useState(null);
    const [matLoading, setMatLoading] = useState(false);

    const customerJobs = useMemo(
        () => allJobs.filter((j) => j.customer_id === customer?.id || j.customer === customer?.name),
        [allJobs, customer]
    );

    const activeJobs = customerJobs.filter((j) => j.status !== "Afsluttet" && j.status !== "Færdig");
    const doneJobs = customerJobs.filter((j) => j.status === "Afsluttet" || j.status === "Færdig");

    // Fetch dedicated /customers/{id}/materials when modal opens
    useEffect(() => {
        if (!open || !customer?.id) { setMatData(null); return; }
        setMatLoading(true);
        fetchCustomerMaterials(customer.id)
            .then((d) => setMatData(d))
            .catch(() => setMatData(null))
            .finally(() => setMatLoading(false));
    }, [open, customer?.id]);

    // Fallback: derive from job details if dedicated endpoint returns nothing
    const fallbackMaterials = useMemo(() => {
        const list = [];
        customerJobs.forEach((j) => {
            const det = allJobDetails[j.id];
            (det?.materials || []).forEach((m) => list.push({
                ...m,
                job_id: j.id,
                sell_total: safeNum(m.sell_total_inc_vat ?? m.sell_total ?? (safeNum(m.qty) * safeNum(m.sell_price_inc_vat ?? m.sell_price ?? m.price))),
            }));
        });
        return list;
    }, [customerJobs, allJobDetails]);

    const apiItems = (matData?.items) || [];
    const allMaterials = apiItems.length > 0 ? apiItems : fallbackMaterials;
    const materialsTotal = safeNum(matData?.total) || allMaterials.reduce((s, m) => s + safeNum(m.sell_total ?? m.sell_total_inc_vat ?? 0), 0);

    if (!customer) return null;

    const q = (s) => (s || "").toString().toLowerCase();
    const filteredJobs = customerJobs.filter((j) => [j.id, j.task, j.status, j.employee, j.address].some((v) => q(v).includes(q(jobSearch))));
    const filteredMaterials = allMaterials.filter((m) => q(m.name).includes(q(matSearch)));
    const filteredInvoices = (customer.invoices || []).filter((i) => `${i.id || ""} ${i.job_id || ""}`.toLowerCase().includes(q(invSearch)));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{customer.name || "Ukendt kunde"}</DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                    <div className="bg-slate-50 rounded-xl p-3 text-sm">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Kontakt</p>
                        <p className="flex items-center gap-1.5 text-slate-700"><Phone className="w-3.5 h-3.5 text-slate-400" />{orDash(customer.phone)}</p>
                        <p className="flex items-center gap-1.5 text-slate-700"><Mail className="w-3.5 h-3.5 text-slate-400" />{orDash(customer.email)}</p>
                        <p className="flex items-center gap-1.5 text-slate-700"><MapPin className="w-3.5 h-3.5 text-slate-400" />{orDash(customer.address)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-sm">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Omsætning</p>
                        <p className="text-2xl font-bold text-emerald-600">{fmtKr(customer.revenue)}</p>
                        <p className="text-xs text-slate-500 mt-1">{customerJobs.length} jobs i alt</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-sm">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Seneste aktivitet</p>
                        <p className="text-slate-800 font-semibold">{orDash(customer.last_job)}</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                            <div><span className="text-slate-400">Aktive</span><p className="font-semibold text-blue-600">{activeJobs.length}</p></div>
                            <div><span className="text-slate-400">Afsluttede</span><p className="font-semibold text-emerald-600">{doneJobs.length}</p></div>
                        </div>
                    </div>
                </div>

                {customer.notes && (
                    <div className="mt-4 bg-amber-50/60 rounded-xl border border-amber-100 p-3 text-sm">
                        <p className="text-[10px] uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1"><StickyNote className="w-3 h-3" />Noter</p>
                        <p className="text-slate-800">{customer.notes}</p>
                    </div>
                )}

                <section className="mt-5">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" />Jobs ({customerJobs.length})</h3>
                        <SearchInput value={jobSearch} onChange={setJobSearch} placeholder="Søg jobs…" testid="customer-job-search" />
                    </div>
                    {filteredJobs.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">{jobSearch ? "Ingen jobs matcher søgningen." : "Ingen jobs endnu."}</p>
                    ) : (
                        <ul className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100 max-h-72 overflow-y-auto">
                            {filteredJobs.map((j) => (
                                <li key={j.id} className="px-3 py-2 flex items-center justify-between text-sm">
                                    <div>
                                        <p className="font-mono text-slate-500">{j.id}</p>
                                        <p className="text-slate-800">{j.task || "—"}{j.address ? <span className="text-slate-400"> · {j.address}</span> : null}</p>
                                    </div>
                                    <StatusPill status={j.status} />
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="mt-5">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Wrench className="w-4 h-4 text-slate-400" />Materialer brugt ({allMaterials.length}){materialsTotal ? <span className="ml-2 text-xs font-normal text-emerald-600">· {fmtKr(materialsTotal)}</span> : null}</h3>
                        <SearchInput value={matSearch} onChange={setMatSearch} placeholder="Søg materiale…" testid="customer-mat-search" />
                    </div>
                    {matLoading ? (
                        <p className="text-sm text-slate-400 italic">Henter materialer…</p>
                    ) : filteredMaterials.length === 0 ? (
                        <p className="text-sm text-slate-400 italic" data-testid="customer-mat-empty">{matSearch ? "Ingen materialer matcher." : "Ingen materialer registreret."}</p>
                    ) : (
                        <ul className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100 max-h-72 overflow-y-auto" data-testid="customer-mat-list">
                            {filteredMaterials.map((m, i) => {
                                const lineTotal = safeNum(m.sell_total ?? m.sell_total_inc_vat ?? (safeNum(m.qty) * safeNum(m.sell_price_inc_vat ?? m.sell_price ?? m.price)));
                                return (
                                    <li key={i} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`customer-mat-row-${i}`}>
                                        <div className="min-w-0">
                                            <p className="text-slate-800 truncate">
                                                {m.name || "Ukendt"}
                                                {m.material_id ? <span className="ml-2 text-[10px] font-mono text-slate-400">{m.material_id}</span> : null}
                                            </p>
                                            <p className="text-xs text-slate-400">
                                                <span className="font-mono">{m.job_id || "—"}</span>
                                                {m.employee_name ? <span> · {m.employee_name}</span> : null}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-slate-700">{safeNum(m.qty)}{m.unit ? ` ${m.unit}` : ""} × {fmtKr(m.sell_price_inc_vat ?? m.sell_price ?? m.price)}</p>
                                            <p className="text-xs font-semibold text-emerald-600">{fmtKr(lineTotal)}</p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                <section className="mt-5">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Banknote className="w-4 h-4 text-slate-400" />Fakturaer ({(customer.invoices || []).length})</h3>
                        <SearchInput value={invSearch} onChange={setInvSearch} placeholder="Søg faktura…" testid="customer-inv-search" />
                    </div>
                    {filteredInvoices.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">{invSearch ? "Ingen fakturaer matcher." : "Ingen fakturaer endnu."}</p>
                    ) : (
                        <ul className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100">
                            {filteredInvoices.map((inv) => (
                                <li key={inv.id} className="px-3 py-2 flex items-center justify-between text-sm">
                                    <div>
                                        <p className="font-mono text-slate-500">{inv.id}</p>
                                        <p className="text-xs text-slate-500">{orDash(inv.date)} · {orDash(inv.job_id)}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold text-emerald-600">{fmtKr(inv.amount ?? inv.total)}</p>
                                        <StatusPill status={inv.status} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <div className="mt-6 flex justify-end">
                    <Button data-testid="customer-detail-close" onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white">Luk</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
